import { APIErrorResponse, isAPIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';

import Identity from './identity';

import { headers, logPrefix } from './constants';
import { UserProfile } from './userProfile';

/**
 * @deprecated
 * Uploads an image to be used as an avatar for the currently logged in user. Validation happens on the client-side to avoid
 * uploading a large image unnecessarily.
 *
 * @param inputField - An input field of `type="file"` (or its ID) which contains a single user selected JPEG (no larger than 3 MB)
 *
 * @returns
 * A _Promise_ resolving to the updated user profile.
 *
 * <details>
 * <summary>
 * On error (throws)
 * <p><span style="background-color:rgb(255, 99, 71); margin-right:1.25em; display:inline-block;"> &nbsp; Deprecated &nbsp; </span></p>
 * </summary>
 *
 * ```
 * MISSING_IMAGE - no image found in the input field provided (client-side end error)
 * IMAGE_TOO_LARGE - image larger than 3 MB (client-side error)
 * UNSUPPORTED_IMAGE_TYPE - Image type not supported (client-side error)
 *
 * ```
 * See documentation for corresponding server error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/User_Registration/uploadProfilePicture *
 * ```
 * </details>
 */
export async function uploadAvatar(
  inputField: HTMLInputElement | string
): Promise<UserProfile | APIErrorResponse> {
  console.warn('[Arc Identity SDK]: uploadAvatar is deprecated');
  return Identity.heartbeat().then(() => {
    let fileInput: HTMLInputElement;

    if (typeof inputField === 'string') {
      fileInput = document.getElementById(inputField.replace(/^#/, '')) as HTMLInputElement;
    } else {
      fileInput = inputField;
    }

    if (!fileInput) {
      throw Error(
        `${logPrefix} element provided not found ${
          typeof inputField === 'string' ? `(${inputField})` : ''
        }.`
      );
    } else if (!fileInput.files || !fileInput.files.length) {
      throw {
        message: `No image found in the input field provided ${
          typeof inputField === 'string' ? `(${inputField})` : ''
        }.`,
        code: 'MISSING_IMAGE'
      }; // using these until we get a matching error on the API side
    } else if (fileInput.files[0].type !== 'image/jpeg') {
      throw { message: 'Image type not supported', code: 'UNSUPPORTED_IMAGE_TYPE' };
    } else if (fileInput.files[0].size > 3e6) {
      throw {
        message: 'Image too big. Only images up to 3 MB are supported.',
        code: 'IMAGE_TOO_LARGE'
      }; // using these until we get a matching error on the API side
    } else {
      const imageFile = fileInput.files[0];
      const body = new FormData();
      body.append('avatar', imageFile);

      return Identity.heartbeat().then(() =>
        fetch(`${Identity.apiOrigin}/identity/public/v1/avatar`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${Identity.userIdentity.accessToken}`
          },
          body
        })
          .then(JSONResponseHandler)
          .then(json => {
            if (isAPIErrorResponse(json)) {
              throw json;
            } else {
              fileInput.value = '';
              Identity.userProfile = json;
              return json;
            }
          })
      );
    }
  });
}

/**
 * @deprecated
 * Deletes the user's avatar image.
 *
 * @returns
 * A _Promise_ resolving to the updated user profile.
 *
 * <details>
 * <summary>
 * On error (throws)
 * <p><span style="background-color:rgb(255, 99, 71); margin-right:1.25em; display:inline-block;"> &nbsp; Deprecated &nbsp; </span></p>
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/User_Registration/deleteProfilePicture#
 * ```
 * </details>
 *
 */
export function deleteAvatar(): Promise<UserProfile | APIErrorResponse> {
  console.warn('[Arc Identity SDK]: deleteAvatar is deprecated');
  return Identity.heartbeat().then(() => {
    return Identity.heartbeat().then(() =>
      fetch(`${Identity.apiOrigin}/identity/public/v1/avatar`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${Identity.userIdentity.accessToken}`,
          ...headers
        }
      })
        .then(JSONResponseHandler)
        .then(json => {
          if (isAPIErrorResponse(json)) {
            throw json;
          } else {
            if (Identity.userProfile) {
              Identity.userProfile.picture = '';
            }
            return json;
          }
        })
    );
  });
}
